const componentActions = new WeakMap();
const componentSelectCleanupFns = new WeakMap();
const blockSelectCleanupFns = new WeakMap();

function parseSection(evt) {
  if (!evt.target) return null;
  const target = evt.target;
  if (!target.id) return null;
  return target.matches('.shopify-section') ? target : null;
}

function parseBlock(evt) {
  const blockId =
    evt.detail && typeof evt.detail === 'object' && 'blockId' in evt.detail && typeof evt.detail.blockId === 'string'
      ? evt.detail.blockId
      : null;

  if (!blockId) return null;

  const target = evt.target;
  const shopifyAttrs = target.getAttribute('data-shopify-editor-block');

  if (!shopifyAttrs) return null;

  try {
    const parsedAttrs = JSON.parse(shopifyAttrs);
    if (parsedAttrs && parsedAttrs.id === blockId) {
      return target;
    }
  } catch {
    return null;
  }

  return null;
}

function parseBlockSection(block, selectors) {
  if (!block) return null;

  const section = block.closest('.shopify-section');
  if (!section) return null;
  return section.querySelector(selectors) ? section : null;
}

/**
 * @param {string} componentSelector - CSS selector for the child components
 * @param {function} initFn - function to initialize each component; receives HTMLElement and returns action handlers
 * @param {object} options - optional; { deferred: boolean }
 * @returns {function} cleanup function
 */
function initComponent(componentSelector, initFn, options) {
  options = options || {};
  const deferred = options.deferred !== undefined ? options.deferred : true;

  function initComponentsInSection(section) {
    const components = section.querySelectorAll(componentSelector);
    components.forEach(el => {
      if (componentActions.has(el)) return; // prevent double init
      const actions = initFn(el);
      componentActions.set(el, actions);
    });
  }

  function unloadComponentsInSection(section) {
    const components = section.querySelectorAll(componentSelector);
    components.forEach(el => {
      const actions = componentActions.get(el);
      if (actions && actions.unload) actions.unload();
      componentActions.delete(el);
    });
  }

  function onSectionLoad(evt) {
    const section = parseSection(evt);
    if (!section) return;
    initComponentsInSection(section);
  }

  function onSectionUnload(evt) {
    const section = parseSection(evt);
    if (!section) return;
    unloadComponentsInSection(section);
  }

  function onSectionSelect(evt) {
    const section = parseSection(evt);
    if (!section) return;

    const components = section.querySelectorAll(componentSelector);
    components.forEach(el => {
      const actions = componentActions.get(el);
      if (!actions || !actions.select) return;

      const cleanup = actions.select();
      if (!cleanup) return;

      componentSelectCleanupFns.set(el, cleanup);
    });
  }

  function onSectionDeselect(evt) {
    const section = parseSection(evt);
    if (!section) return;

    const components = section.querySelectorAll(componentSelector);
    components.forEach(el => {
      const cleanup = componentSelectCleanupFns.get(el);
      if (!cleanup) return;
      cleanup();
      componentSelectCleanupFns.delete(el);
    });
  }

  function onSectionReorder(evt) {
    const section = parseSection(evt);
    if (!section) return;

    const components = section.querySelectorAll(componentSelector);
    components.forEach(el => {
      const actions = componentActions.get(el);
      if (actions && actions.reorder) actions.reorder();
    });
  }

  function onBlockSelect(evt) {
    const block = parseBlock(evt);
    if (!block) return;

    const section = parseBlockSection(block, componentSelector);
    if (!section) return;

    const components = section.querySelectorAll(componentSelector);
    components.forEach(el => {
      const actions = componentActions.get(el);
      if (!actions || !actions.selectBlock) return;

      const cleanup = actions.selectBlock(block);
      if (!cleanup) return;

      blockSelectCleanupFns.set(block, cleanup);
    });
  }

  function onBlockDeselect(evt) {
    const block = parseBlock(evt);
    if (!block) return;

    const cleanup = blockSelectCleanupFns.get(block);
    if (!cleanup) return;

    cleanup();
    blockSelectCleanupFns.delete(block);
  }

  function init() {
    document.addEventListener('shopify:section:load', onSectionLoad);
    document.addEventListener('shopify:section:unload', onSectionUnload);
    document.addEventListener('shopify:section:select', onSectionSelect);
    document.addEventListener('shopify:section:deselect', onSectionDeselect);
    document.addEventListener('shopify:section:reorder', onSectionReorder);
    document.addEventListener('shopify:block:select', onBlockSelect);
    document.addEventListener('shopify:block:deselect', onBlockDeselect);

    // Initialize components on page load within existing sections
    document.querySelectorAll('.shopify-section').forEach(section => {
      initComponentsInSection(section);
    });

    // Observe new elements
    const observer = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType !== 1) return;

          if (node.matches?.(componentSelector)) {
            if (!componentActions.has(node)) {
              const actions = initFn(node);
              componentActions.set(node, actions);
            }
          } else {
            const inner = node.querySelectorAll?.(componentSelector);
            inner?.forEach(el => {
              if (!componentActions.has(el)) {
                const actions = initFn(el);
                componentActions.set(el, actions);
              }
            });
          }
        });
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return function cleanup() {
      document.querySelectorAll('.shopify-section').forEach(section => {
        unloadComponentsInSection(section);
      });

      document.removeEventListener('shopify:section:load', onSectionLoad);
      document.removeEventListener('shopify:section:unload', onSectionUnload);
      document.removeEventListener('shopify:section:select', onSectionSelect);
      document.removeEventListener('shopify:section:deselect', onSectionDeselect);
      document.removeEventListener('shopify:section:reorder', onSectionReorder);
      document.removeEventListener('shopify:block:select', onBlockSelect);
      document.removeEventListener('shopify:block:deselect', onBlockDeselect);
      observer.disconnect();

    };
  }

  if (!deferred) {
    return init();
  }

  let deferredCleanup = null;
  const timeout = setTimeout(() => {
    deferredCleanup = init();
  }, 0);

  return function cleanup() {
    if (deferredCleanup) deferredCleanup();
    clearTimeout(timeout);
  };
}

export { initComponent };
